import type React from "react";
import { memo, useEffect, useMemo, useRef, useState, type FC } from "react";
import { Text } from "@/components/podkit/typography/Text";
import { Pill } from "@/components/podkit/status/Pill";
import { Button } from "@/components/flexkit/Button";
import {
    useIsUserAuthenticatedWithRunnerForHost,
    useRunner,
    useSearchRepositoriesPaginated,
    type PlainSearchRepository,
} from "@/queries/runner-queries";
import { CornerDownLeft } from "lucide-react";
import { withGitpodSize } from "@/assets/icons/utils";
import { SearchMode } from "gitpod-next-api/gitpod/v1/runner_pb";
import IconTasks from "central-icons/IconTasks";
import IconCrossSmall from "central-icons/IconCrossSmall";
import { useListRunnerSCMIntegrations, type PlainSCMIntegration } from "@/queries/runner-configuration-queries";
import { ErrorMessage } from "@/components/ErrorMessage";
import { getScmDisplayName } from "@/components/automations/scm-utils";
import { RepoTriggerSampleDialog } from "@/components/automations/RepoTriggerSampleDialog";
import { Input } from "@/components/podkit/forms/Input";
import { Banner } from "@/components/flexkit/Banner";
import { AuthenticationModal } from "@/components/environments/onboarding/CreateProjectFromEnvironmentModal";
import { TrackLocations } from "@/hooks/use-segment";
import { Select } from "@/components/flexkit/select/Select";

export const RepoFilteringContent: FC<{
    repoSearchString: string;
    runnerId: string;
    disabled: boolean;
    scmHost: string;
    onAddFilter: (filter: string) => void;
    onClearFilter: () => void;
}> = memo(({ disabled, repoSearchString, runnerId, scmHost: selectedScmHost, onAddFilter, onClearFilter }) => {
        const [showAuthDialog, setShowAuthDialog] = useState(false);

        const {
            data: scmIntegrations,
            isLoading: isLoadingSCMIntegrations,
            error: listSCMIntegrationsError,
            refetch: refetchSCMIntegrations,
        } = useListRunnerSCMIntegrations(runnerId);

        const { data: runner } = useRunner(runnerId);

        let scmHost: string;
        let scmId: string;
        if (scmIntegrations && scmIntegrations.length > 0) {
            // find the SCM integration that matches the selected SCM host
            const selected = scmIntegrations.find((it) => it.host === selectedScmHost);
            if (selected) {
                scmHost = selected.host;
                scmId = selected.scmId;
            } else {
                // if the selected SCM host is not found in the list of SCM integrations, use the first integration
                scmHost = scmIntegrations[0].host;
                scmId = scmIntegrations[0].scmId;
            }
        } else {
            scmHost = "";
            scmId = "";
        }

        const shouldCheckAuth = !!runnerId && !!scmHost;
        const {
            data: authData,
            isPending: isPendingAuthData,
            error: authDataError,
        } = useIsUserAuthenticatedWithRunnerForHost(runnerId, scmHost, {
            enabled: shouldCheckAuth,
            retryOnFailedPrecondition: true,
            refetchUntilAuthenticated: true,
        });
        const missingAuthentication =
            shouldCheckAuth && !isPendingAuthData && authData?.type === "AuthenticationRequired";

        const {
            data: repositoriesData,
            isLoading: isSearching,
            error: searchRepositoriesError,
            refetch: refetchRepositories,
            fetchNextPage,
            hasNextPage,
            isFetchingNextPage,
        } = useSearchRepositoriesPaginated({
            runnerId,
            query: repoSearchString,
            scmHost,
            enabled: !disabled && scmHost !== "" && repoSearchString !== "",
            searchMode: SearchMode.NATIVE,
        });

        // Flatten paginated results and extract total count from first page
        const repositories = useMemo(
            () => repositoriesData?.pages.flatMap((page) => page.repositories) ?? [],
            [repositoriesData],
        );
        const totalCount = repositoriesData?.pages[0]?.totalCount ?? -1;

        // Early returns for cases where SCM host selector should not be shown
        if (isLoadingSCMIntegrations) {
            return (
                <Text className="animate-pulse text-base text-content-secondary">
                    Finding suitable repository provider…
                </Text>
            );
        }

        if (listSCMIntegrationsError) {
            return (
                <div className="flex flex-row items-baseline gap-2">
                    <ErrorMessage error={listSCMIntegrationsError} />
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            void refetchSCMIntegrations();
                        }}
                        data-tracking-id="retry"
                    >
                        Retry
                    </Button>
                </div>
            );
        }

        if (scmIntegrations && scmIntegrations.length === 0) {
            return (
                <div className="flex items-center justify-between rounded-lg border border-border-base p-3">
                    <Text className="text-content-muted">
                        {runner?.name} does not support repository trigger. Please select a different runner.
                    </Text>
                </div>
            );
        }

        // Determine the content based on current state
        let content: React.ReactNode;

        if (shouldCheckAuth && isPendingAuthData) {
            content = (
                <div className="flex items-center justify-between rounded-lg border border-border-base p-3">
                    <Text className="text-content-muted">Checking authentication</Text>
                </div>
            );
        } else if (shouldCheckAuth && !isPendingAuthData && authDataError && !missingAuthentication) {
            content = <ErrorMessage error={authDataError} />;
        } else if (isSearching) {
            content = <Text className="animate-pulse text-base text-content-secondary">Searching repositories…</Text>;
        } else if (repositories.length > 0) {
            content = (
                <RepoFilterResult
                    searchString={repoSearchString}
                    repositories={repositories}
                    totalCount={totalCount}
                    onClear={onClearFilter}
                    hasNextPage={hasNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    onLoadMore={() => void fetchNextPage()}
                />
            );
        } else if (missingAuthentication) {
            content = (
                <>
                    <ErrorMessage error={authDataError} />
                    <Banner
                        action={{
                            text: "Authenticate",
                            onClick: () => setShowAuthDialog(true),
                            "data-tracking-id": "automation-edit-code-context-scm-authentication",
                        }}
                        variant="warning"
                        text={
                            <span>
                                <span className="font-bold">{runner?.name || "Unknown runner"}</span> requires
                                authentication with <span className="font-bold">{scmHost}</span>
                            </span>
                        }
                        className="shadow-none"
                    />
                    {scmHost && runnerId && authData && showAuthDialog && (
                        <AuthenticationModal
                            repoURL={"https://" + scmHost}
                            authResponse={authData}
                            runnerId={runnerId}
                            onAuthSuccess={() => {
                                setShowAuthDialog(false);
                            }}
                            onClose={() => {
                                setShowAuthDialog(false);
                            }}
                            data-track-location={TrackLocations.GitAuthenticationTokenCreateModal}
                        />
                    )}
                </>
            );
        } else {
            content = (
                <RepoFilterInput
                    initialSearchString={repoSearchString}
                    loading={isSearching}
                    disabled={disabled}
                    onAddFilter={onAddFilter}
                    searchError={searchRepositoriesError}
                    onRetry={refetchRepositories}
                    scmId={scmId}
                />
            );
        }

        return content;
    },
);
RepoFilteringContent.displayName = "RepoFilteringContent";

const isBitbucketScm = (scmId: string): boolean => {
    return scmId.toLowerCase() === "bitbucket";
};

export type SCMHostSelectorProps = {
    scmIntegrations: PlainSCMIntegration[];
    selectedHost: string;
    onHostChange: (host: string) => void;
    disabled: boolean;
};

export const SCMHostSelector: FC<SCMHostSelectorProps> = memo(
    ({ scmIntegrations, selectedHost, onHostChange, disabled }) => {
        return (
            <Select
                value={selectedHost}
                onValueChange={onHostChange}
                disabled={disabled}
                data-testid="scm-host-selector"
                className="min-w-0 flex-1"
            >
                {scmIntegrations.map((integration) => {
                    const isBitbucket = isBitbucketScm(integration.scmId);
                    return (
                        <Select.Item
                            key={integration.id}
                            value={integration.host}
                            disabled={isBitbucket}
                            className={isBitbucket ? "opacity-60" : undefined}
                        >
                            <span className="flex items-center gap-2">
                                {integration.host}
                                {isBitbucket && (
                                    <Pill variant="neutral" size="sm">
                                        Unsupported
                                    </Pill>
                                )}
                            </span>
                        </Select.Item>
                    );
                })}
            </Select>
        );
    },
);
SCMHostSelector.displayName = "SCMHostSelector";

const RepoFilterInput: FC<{
    initialSearchString: string;
    loading: boolean;
    disabled: boolean;
    onAddFilter: (filter: string) => void;
    searchError?: Error | null;
    onRetry?: () => void;
    scmId?: string;
}> = memo(({ initialSearchString, loading, disabled, onAddFilter, searchError, onRetry, scmId }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [showError, setShowError] = useState(!!searchError);

    useEffect(() => {
        setShowError(!!searchError);
    }, [searchError]);

    const submitSearchString = () => {
        if (inputRef.current) {
            const value = inputRef.current.value;
            if (searchError && value === initialSearchString && onRetry) {
                // Same search string and there was an error - retry the query
                onRetry();
            } else {
                onAddFilter(value);
            }
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            submitSearchString();
        }
    };

    const handleChange = () => {
        if (showError) {
            setShowError(false);
        }
    };

    const scmDisplayName = scmId ? getScmDisplayName(scmId) : "repository";
    const placeholderText =
        scmDisplayName === "repository"
            ? "Describe what repositories to include or paste a filter"
            : `Describe what repositories to include or paste a ${scmDisplayName} filter`;

    return (
        <div className="flex w-full flex-col gap-2">
            <div className="flex w-full flex-row items-center gap-2">
                <Input
                    ref={inputRef}
                    disabled={disabled}
                    defaultValue={initialSearchString}
                    type="text"
                    placeholder={placeholderText}
                    className="max-w-none"
                    onKeyDown={handleKeyDown}
                    onChange={handleChange}
                />
                <Button
                    type="button"
                    variant="primary"
                    size="md"
                    className="aspect-square p-0"
                    loading={loading}
                    disabled={disabled}
                    LeadingIcon={withGitpodSize(CornerDownLeft)}
                    onClick={submitSearchString}
                    data-tracking-id="submitsearchstring"
                >
                    <span className="sr-only">Add repository search filter</span>
                </Button>
            </div>
            {showError && searchError && <ErrorMessage error={searchError} />}
        </div>
    );
});
RepoFilterInput.displayName = "RepoFilterInput";

const RepoFilterResult: FC<{
    searchString: string;
    repositories: PlainSearchRepository[];
    totalCount: number;
    onClear: () => void;
    hasNextPage?: boolean;
    isFetchingNextPage?: boolean;
    onLoadMore?: () => void;
}> = memo(({ searchString, repositories, totalCount, onClear, hasNextPage, isFetchingNextPage, onLoadMore }) => {
    const [openRepoTriggerSampleDialog, setOpenRepoTriggerSampleDialog] = useState(false);

    // Format the count text based on whether we know the total
    const countText = useMemo(() => {
        if (totalCount > 0) {
            return `${repositories.length} of ${totalCount} matching repositories`;
        }
        if (hasNextPage) {
            // Total unknown but more pages exist
            return `${repositories.length}+ matching repositories`;
        }
        return `${repositories.length} matching repositories`;
    }, [repositories.length, totalCount, hasNextPage]);

    return (
        <>
            <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between rounded-lg border border-border-base p-3">
                    <Pill variant="brand" size="md">
                        {searchString}
                    </Pill>
                    <Button
                        variant="ghost"
                        size="sm"
                        LeadingIcon={withGitpodSize(IconCrossSmall)}
                        onClick={onClear}
                        data-tracking-id="code-context-repo-search-clear"
                    >
                        Clear
                    </Button>
                </div>
                <div className="flex flex-row items-center gap-2">
                    <Text className="flex-1 text-content-secondary">{countText}</Text>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        LeadingIcon={withGitpodSize(IconTasks)}
                        data-tracking-id="code-context-repo-search-view-sample"
                        onClick={() => {
                            setOpenRepoTriggerSampleDialog(true);
                        }}
                    >
                        View sample
                    </Button>
                </div>
            </div>
            <RepoTriggerSampleDialog
                repositories={repositories}
                open={openRepoTriggerSampleDialog}
                onOpenChange={setOpenRepoTriggerSampleDialog}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                onLoadMore={onLoadMore}
            />
        </>
    );
});
RepoFilterResult.displayName = "RepoFilterResult";
